import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CaretLeft,
  CaretRight,
  Check,
  Globe,
  Package,
  Plus,
} from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRFQ } from '@/contexts/RFQContext';
import { SITE_LOGO_SRC } from '@/constants/branding';
import { getSubcategoryIconComponent } from '@/constants/categorySubmenuIcons';
import {
  PRODUCT_IMG_REFERRER_POLICY,
  applyProductImageFallback,
  buildImageFallbackChain,
  pickProductHeroPreviewUrl,
  productImageChainDataAttrs,
} from '@/utils/productImage';
import './mega-menu-panel.css';

/** SVG yüklenemezse (404, yanlış MIME) Phosphor yedeğine düşer. */
function SubcategoryMenuIcon({ svgSrc, IconComp }) {
  const [svgFailed, setSvgFailed] = useState(false);
  if (svgSrc && !svgFailed) {
    return (
      <img
        src={svgSrc}
        alt=""
        className="category-item__icon category-item__icon--svg"
        loading="lazy"
        decoding="async"
        onError={() => setSvgFailed(true)}
      />
    );
  }
  return <IconComp className="category-item__icon" size={26} weight="duotone" />;
}

/**
 * DeMaxtore mega menü sağ paneli.
 * Görseller ve linkler tamamen props ile gelir (hardcoded demo URL yok).
 */

export function MegaMenuPanel({
  sectionTitle,
  /** WoodMart tarzı: grid üstü başlık (kategori adı) */
  gridTitle,
  gridSubtitle,
  viewAllLabel,
  onViewAll,
  /** 2 = varsayılan; Food mega menüde 3 sütun (panel genişliği aynı, daha sıkı hücre) */
  subcategoryGridColumns = 2,
  subcategories = [],
  onSubcategorySelect,
  promo = null,
  products = [],
  loading = false,
  lang = 'en',
  currency = 'USD',
  /** featured products section label */
  featuredLabel = 'Featured',
  /** empty state when no products */
  emptyFeaturedMessage,
  onProductClick,
  getProductName,
  /** Primary CTA under product cards (e.g. RFQ) */
  actionLabel,
  /** Kartta başlığın altındaki küçük gri satır (ör. kategori) */
  productLineHint,
  /** Alt kategori anahtarı → SVG URL; dönüş yoksa Phosphor ikon kullanılır (ör. Food mega menü) */
  resolveSubcategoryIconSrc,
  className = '',
}) {
  const { t } = useLanguage();
  const { addItem } = useRFQ();
  const trackRef = useRef(null);
  const addedTimersRef = useRef(new Map());
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    setAddedIds(new Set());
    addedTimersRef.current.forEach((tid) => clearTimeout(tid));
    addedTimersRef.current.clear();
  }, [products]);

  useEffect(
    () => () => {
      addedTimersRef.current.forEach((tid) => clearTimeout(tid));
      addedTimersRef.current.clear();
    },
    [],
  );

  const handleAddToRfq = async (e, product) => {
    e.stopPropagation();
    const res = await addItem(product);
    if (!res.ok) return;
    const id = product.id;
    setAddedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const existing = addedTimersRef.current.get(id);
    if (existing) clearTimeout(existing);
    const tid = window.setTimeout(() => {
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      addedTimersRef.current.delete(id);
    }, 2000);
    addedTimersRef.current.set(id, tid);
  };

  const updateNav = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanPrev(scrollLeft > 2);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  const scrollByDir = (dir) => {
    trackRef.current?.scrollBy({ left: dir * 220, behavior: 'smooth' });
    window.setTimeout(updateNav, 350);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const rafId = requestAnimationFrame(() => updateNav());
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateNav()) : null;
    ro?.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro?.disconnect();
    };
  }, [products, loading, updateNav]);

  const resolveName = (p) => {
    if (typeof getProductName === 'function') return getProductName(p, lang);
    return p.name || '';
  };

  return (
    <div className={`mega-menu-panel mega-menu-panel--header-theme mega-menu-panel--woodmart flex min-h-0 flex-col gap-4 ${className}`}>
      {sectionTitle ? (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-800">{sectionTitle}</h3>
      ) : null}

      {(gridTitle || gridSubtitle || viewAllLabel) && (
        <div className="mega-menu-panel__grid-head flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            {gridTitle ? (
              <h3 className="font-sans text-lg font-bold leading-tight tracking-tight text-[#0c2340] sm:text-xl">
                {gridTitle}
              </h3>
            ) : null}
            {gridSubtitle ? (
              <p className="mt-0.5 font-sans text-xs text-neutral-500">{gridSubtitle}</p>
            ) : null}
          </div>
          {viewAllLabel && typeof onViewAll === 'function' ? (
            <button
              type="button"
              onClick={onViewAll}
              className="mega-menu-panel__view-all mt-1 inline-flex w-fit items-center gap-1 rounded-lg px-2 py-1 font-sans text-xs font-semibold text-[#1e3a5f] underline-offset-2 hover:text-[#c62828] hover:underline sm:mt-0"
            >
              {viewAllLabel}
            </button>
          ) : null}
        </div>
      )}

      {subcategories.length > 0 ? (
        <div
          className={`category-grid category-grid--woodmart ${
            subcategoryGridColumns === 3 ? 'category-grid--cols-3' : ''
          }`}
        >
          {subcategories.map((cat, idx) => {
            const key = cat.listKey || `${cat.key || cat.slug || cat.label || 'cat'}-${idx}`;
            if (cat.gridSpacer) {
              return (
                <div
                  key={key}
                  className="category-grid__spacer"
                  aria-hidden
                />
              );
            }
            const IconComp = getSubcategoryIconComponent(cat.key);
            const svgSrc =
              typeof resolveSubcategoryIconSrc === 'function' ? resolveSubcategoryIconSrc(cat.key) : null;
            return (
              <button
                key={key}
                type="button"
                className="category-item category-item--row"
                onClick={() => onSubcategorySelect?.(cat)}
              >
                <div className="category-item__icon-wrap category-item__icon-wrap--row" aria-hidden>
                  <SubcategoryMenuIcon svgSrc={svgSrc} IconComp={IconComp} />
                </div>
                <span className="category-item__label category-item__label--row">{cat.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {promo && promo.variant === 'soft' ? (
        <div className="promo-soft">
          <div className="promo-soft__main">
            <div className="promo-soft__copy">
              {promo.kicker ? (
                <p className="promo-soft__kicker">{promo.kicker}</p>
              ) : null}
              {promo.title ? <p className="promo-soft__title">{promo.title}</p> : null}
              {promo.subtitle ? <p className="promo-soft__subtitle">{promo.subtitle}</p> : null}
              <div className="promo-soft__ctas">
                {promo.ctaSecondary ? (
                  <button type="button" onClick={promo.onSecondary} className="promo-soft__btn promo-soft__btn--light">
                    {promo.ctaSecondary}
                  </button>
                ) : null}
                {promo.ctaPrimary ? (
                  <button type="button" onClick={promo.onPrimary} className="promo-soft__btn promo-soft__btn--red">
                    {promo.ctaPrimary}
                  </button>
                ) : null}
              </div>
            </div>
            <div className="promo-soft__deco" aria-hidden>
              <span className="promo-soft__blob promo-soft__blob--a" />
              <span className="promo-soft__blob promo-soft__blob--b" />
              <Globe className="promo-soft__deco-icon promo-soft__deco-icon--lg" size={72} weight="duotone" />
              <Package className="promo-soft__deco-icon promo-soft__deco-icon--sm" size={44} weight="duotone" />
            </div>
          </div>
        </div>
      ) : promo && promo.variant === 'gradient' ? (
        <div className="mb-0 overflow-hidden rounded-2xl bg-gradient-to-r from-[#1e3a5f] via-[#162f4d] to-[#0f2340] p-4 text-white shadow-lg shadow-[#1e3a5f]/20">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex max-w-xl flex-1 items-start gap-3">
              <div className="promo-gradient__icon flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/12">
                <Globe className="text-white/95" size={36} weight="duotone" aria-hidden />
              </div>
              <div>
                {promo.kicker ? (
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">{promo.kicker}</p>
                ) : null}
                {promo.subtitle ? (
                  <p className="mt-1 text-sm font-medium leading-snug">{promo.subtitle}</p>
                ) : null}
              </div>
            </div>
            <div className="flex w-full flex-shrink-0 flex-wrap gap-2 sm:w-auto sm:justify-end">
              {promo.ctaPrimary ? (
                <button
                  type="button"
                  onClick={promo.onPrimary}
                  className="inline-flex items-center justify-center rounded-full bg-[#e53935] px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-md transition hover:bg-[#c62828]"
                >
                  {promo.ctaPrimary}
                </button>
              ) : null}
              {promo.ctaSecondary ? (
                <button
                  type="button"
                  onClick={promo.onSecondary}
                  className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/10 px-4 py-2.5 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
                >
                  {promo.ctaSecondary}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : promo && (promo.title || promo.subtitle || promo.kicker) ? (
        <div className="promo-banner promo-banner--icon-only border border-neutral-100 shadow-sm">
          <div className="promo-banner__icon-strip flex items-center justify-center bg-gradient-to-br from-[#f0f4fa] to-[#e2e8f0] py-6">
            <Globe className="text-[#1e3a5f]" size={48} weight="duotone" aria-hidden />
          </div>
          <div className="promo-banner__overlay !relative !bg-transparent !p-3 sm:!p-4">
            <div className="rounded-lg bg-black/55 px-3 py-2 backdrop-blur-sm sm:px-4 sm:py-2.5">
              {promo.kicker ? (
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/90">{promo.kicker}</p>
              ) : null}
              {promo.title ? <p className="text-sm font-semibold text-white sm:text-base">{promo.title}</p> : null}
              {promo.subtitle ? (
                <p className="mt-0.5 text-[11px] leading-snug text-white/85 sm:text-xs">{promo.subtitle}</p>
              ) : null}
              {(promo.ctaPrimary || promo.ctaSecondary) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {promo.ctaPrimary ? (
                    <button
                      type="button"
                      onClick={promo.onPrimary}
                      className="rounded-full bg-[#e53935] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white shadow hover:bg-[#c62828]"
                    >
                      {promo.ctaPrimary}
                    </button>
                  ) : null}
                  {promo.ctaSecondary ? (
                    <button
                      type="button"
                      onClick={promo.onSecondary}
                      className="rounded-full border border-white/35 bg-white/10 px-3 py-1.5 text-[10px] font-semibold text-white backdrop-blur-sm hover:bg-white/20"
                    >
                      {promo.ctaSecondary}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mega-menu-panel__featured">
        <div className="mb-2 flex items-center justify-between gap-2 border-t border-neutral-200/80 pt-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0c2340]/70">{featuredLabel}</p>
          {products.length > 1 ? (
            <div className="product-carousel__nav">
              <button
                type="button"
                aria-label="Previous"
                disabled={!canPrev}
                onClick={() => scrollByDir(-1)}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 shadow-sm hover:bg-neutral-50"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
              <button
                type="button"
                aria-label="Next"
                disabled={!canNext}
                onClick={() => scrollByDir(1)}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 shadow-sm hover:bg-neutral-50"
              >
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 w-[9.5rem] shrink-0 animate-pulse rounded-xl bg-neutral-100" />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="product-carousel">
            <div ref={trackRef} className="product-carousel__track" onScroll={updateNav}>
              {products.map((p) => {
                const name = resolveName(p);
                const isAdded = addedIds.has(p.id);
                const menuImgChain = buildImageFallbackChain(
                  pickProductHeroPreviewUrl(p) || SITE_LOGO_SRC,
                  'card',
                );

                return (
                  <div key={p.id} className="product-card product-card--woodmart">
                    <button
                      type="button"
                      className="block w-full text-left"
                      onClick={() => onProductClick?.(p)}
                    >
                      <div className="product-card__media">
                        <img
                          src={menuImgChain[0]}
                          alt=""
                          width={200}
                          height={200}
                          loading="lazy"
                          decoding="async"
                          sizes="(max-width: 768px) 40vw, 9.5rem"
                          referrerPolicy={PRODUCT_IMG_REFERRER_POLICY}
                          onError={applyProductImageFallback}
                          {...productImageChainDataAttrs(menuImgChain)}
                        />
                      </div>
                      <div className="product-card__body">
                        <p className="product-card__title">{name}</p>
                        {productLineHint ? (
                          <p className="product-card__meta product-card__meta--hint">{productLineHint}</p>
                        ) : null}
                      </div>
                    </button>
                    <div className="product-card__actions px-2 pb-2">
                      <button
                        type="button"
                        className={`product-card__btn${isAdded ? ' product-card__btn--added' : ''}`}
                        onClick={(e) => handleAddToRfq(e, p)}
                        data-testid={`mega-menu-add-rfq-${p.id}`}
                      >
                        {isAdded ? (
                          <>
                            <Check size={14} weight="bold" className="product-card__btn-icon" aria-hidden />
                            <span>{t('added')}</span>
                          </>
                        ) : (
                          <>
                            <Plus size={14} weight="bold" className="product-card__btn-icon" aria-hidden />
                            <span>{actionLabel ?? 'Add to RFQ'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-xs text-neutral-400">{emptyFeaturedMessage}</p>
        )}
      </div>
    </div>
  );
}

export default MegaMenuPanel;
